import { useState, useCallback } from 'react'
import type { 
  ConsultationInput, 
  ConsultationResult,
  ConsultationMessage 
} from '@creanizer/types'
import { useConsultationStore } from '../../../stores/consultationStore'
import { useSettingsStore } from '../../../stores/settingsStore'
import { apiClient, ApiError } from '../../../services/api'
import { useGeneratorModel } from '../components/GeneratorModelSelector'

export function useConsultation() {
  const consultationStore = useConsultationStore()
  const settingsStore = useSettingsStore()
  const modelId = useGeneratorModel('consultation')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  const sendMessage = useCallback(async (
    message: string,
    options?: {
      images?: string[]  // 追加: 画像データ（base64）
      personaSettings?: ConsultationInput['personaSettings']
      conferenceSettings?: ConsultationInput['conferenceSettings']
      context?: ConsultationInput['context']
      criticalMode?: boolean
      useGrounding?: boolean
      temperature?: number
      avoidElements?: string[]  // 禁止ワード・表現
    }
  ): Promise<ConsultationResult | null> => {
    setIsLoading(true)
    setError(null)
    
    let session: ReturnType<typeof consultationStore.createSession> | null = null
    
    try {
      // APIキーの確認
      if (!settingsStore.isApiKeySet()) {
        throw new Error('APIキーが設定されていません')
      }
      
      // アクティブセッションがない場合は作成
      session = consultationStore.getCurrentActiveSession()
      if (!session) {
        consultationStore.createSession()
        // createSessionの後、最新のactiveSessionを取得
        session = consultationStore.getCurrentActiveSession()
        if (!session) {
          throw new Error('セッションの作成に失敗しました')
        }
      }
      
      // 追加前の履歴を保存
      const previousMessages = [...session.messages]
      
      // ユーザーメッセージを追加
      const userMessage: ConsultationMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        role: 'user',
        content: message,
        images: options?.images,  // 画像データを含める
        timestamp: new Date().toISOString()
      }
      consultationStore.addMessage(session.id, userMessage)
      
      // リクエストの構築
      const personaSettings = options?.conferenceSettings?.enabled 
        ? undefined  // 会議モードの場合はペルソナ設定を無効化
        : (options?.personaSettings || consultationStore.personaSettings || undefined)
      
      // ユーザー設定をコンテキストに追加
      let userContext = ''
      if (consultationStore.userSettings?.enabled) {
        const userName = consultationStore.userSettings.name || 'ユーザー'
        userContext = `【ユーザー情報】\n名前: ${userName}\n`
        if (consultationStore.userSettings.profile) {
          userContext += `プロフィール: ${consultationStore.userSettings.profile}\n`
        }
        userContext += '\n'
      }

      // ロールプレイ設定をコンテキストに追加
      let roleplayContext = ''
      if (consultationStore.roleplaySettings?.enabled) {
        roleplayContext = `【ロールプレイモード】\n`
        roleplayContext += `このセッションはロールプレイモードです。相談ではなく、その場面を演じてください。\n`
        if (consultationStore.roleplaySettings.location) {
          roleplayContext += `場所・時間: ${consultationStore.roleplaySettings.location}\n`
        }
        if (consultationStore.roleplaySettings.situation) {
          roleplayContext += `シチュエーション: ${consultationStore.roleplaySettings.situation}\n`
        }
        roleplayContext += `キャラクターは自然にその場にいるように振る舞い、会話を進めてください。\n\n`
      }

      const input: ConsultationInput = {
        message,
        images: options?.images,  // 画像データを追加
        sessionId: session.id,
        personaSettings,
        conferenceSettings: options?.conferenceSettings,
        roleplaySettings: consultationStore.roleplaySettings || undefined,
        context: {
          ...options?.context,
          customContext: roleplayContext + userContext + (options?.context?.customContext || '')
        },
        options: {
          criticalMode: options?.criticalMode,
          useGrounding: options?.useGrounding,
          temperature: options?.temperature,
          maxTokens: 60000, // 重要: 60000以上必須
          useGemini3Preview: consultationStore.settings?.useGemini3Preview ?? false,
          useGemini3FlashPreview: consultationStore.settings?.useGemini3FlashPreview ?? false,
          useGemini3FlashLitePreview: consultationStore.settings?.useGemini3FlashLitePreview ?? false,
          avoidElements: options?.avoidElements
        },
        chatHistory: previousMessages  // 追加前の履歴を使用
      }

      // デバッグ: 送信データの確認
      console.log('[sendMessage] Images count:', options?.images?.length || 0)
      if (options?.images && options.images.length > 0) {
        options.images.forEach((img, i) => {
          console.log(`[sendMessage] Image ${i}: ${img.substring(0, 50)}... (length: ${img.length})`)
        })
      }

      // apiClientを使用してAPIリクエスト
      const response = await apiClient.consultationChat({ ...input, modelId })
      console.log('API Response:', response)
      
      if (response.success && response.data) {
        // アシスタントメッセージを追加（既にフォーマット済みのメッセージを使用）
        consultationStore.addMessage(session.id, response.data.message)
        
        // ConsultationResult型で返す
        const result: ConsultationResult = {
          message: response.data.message,
          sessionId: response.data.sessionId,
          metadata: {
            processingTime: undefined,
            modelUsed: response.data.metadata?.modelUsed,
            tokenCount: response.data.metadata?.tokenCount
          }
        }
        
        return result
      } else {
        console.error('Response not successful:', response)
        throw new Error('応答の生成に失敗しました')
      }
      
    } catch (err) {
      console.error('Error in sendMessage:', err)
      
      // ApiErrorの場合、次のAPIキーの存在を知らせる
      if (err instanceof ApiError && err.isRateLimit) {
        const nextApiKey = settingsStore.getNextAvailableApiKey()
        if (nextApiKey) {
          const errorMessage = `レート制限エラー: 別のAPIキー「${nextApiKey.name}」が利用可能です。設定画面で切り替えてください。`
          setError(errorMessage)
        } else {
          setError('レート制限エラー: 利用可能なAPIキーがありません。しばらくお待ちください。')
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : '不明なエラーが発生しました'
        setError(errorMessage)
      }
      
      return null
    } finally {
      setIsLoading(false)
    }
  }, [consultationStore, settingsStore, modelId])
  
  const regenerateLastMessage = useCallback(async (options?: {
    personaSettings?: ConsultationInput['personaSettings']
    conferenceSettings?: ConsultationInput['conferenceSettings']
    context?: ConsultationInput['context']
    criticalMode?: boolean
    temperature?: number
    avoidElements?: string[]
  }): Promise<ConsultationResult | null> => {
    const session = consultationStore.getCurrentActiveSession()
    if (!session || session.messages.length === 0) {
      setError('再生成するメッセージがありません')
      return null
    }
    
    // 最後のアシスタントメッセージを削除
    const lastAssistantIndex = session.messages.findLastIndex(m => m.role === 'assistant')
    if (lastAssistantIndex === -1) {
      setError('再生成するメッセージがありません')
      return null
    }
    
    const lastAssistantMessage = session.messages[lastAssistantIndex]
    consultationStore.deleteMessage(session.id, lastAssistantMessage.id)
    
    // 最後のユーザーメッセージを取得して再送信
    const lastUserMessage = session.messages.findLast(m => m.role === 'user')
    if (!lastUserMessage) {
      setError('ユーザーメッセージが見つかりません')
      return null
    }
    
    // 元のメッセージと同じ設定で再送信（maxTokens: 60000も含む）
    return sendMessage(lastUserMessage.content, options)
  }, [consultationStore, sendMessage])
  
  const retryUserMessage = useCallback(async (
    messageId: string,
    options?: {
      personaSettings?: ConsultationInput['personaSettings']
      conferenceSettings?: ConsultationInput['conferenceSettings']
      context?: ConsultationInput['context']
      criticalMode?: boolean
      useGrounding?: boolean
      temperature?: number
      avoidElements?: string[]
    }
  ): Promise<ConsultationResult | null> => {
    setIsLoading(true)
    setError(null)
    
    const session = consultationStore.getCurrentActiveSession()
    if (!session || session.messages.length === 0) {
      setError('再送信するメッセージがありません')
      setIsLoading(false)
      return null
    }
    
    // 指定されたメッセージを探す
    const messageIndex = session.messages.findIndex(m => m.id === messageId)
    if (messageIndex === -1) {
      setError('メッセージが見つかりません')
      setIsLoading(false)
      return null
    }
    
    const targetMessage = session.messages[messageIndex]
    if (targetMessage.role !== 'user') {
      setError('ユーザーメッセージのみ再送信できます')
      setIsLoading(false)
      return null
    }
    
    try {
      // APIキーの確認
      if (!settingsStore.isApiKeySet()) {
        throw new Error('APIキーが設定されていません')
      }
      
      // 編集後の最新の内容を取得（編集されていれば編集後、そうでなければ元の内容）
      const currentSession = consultationStore.getCurrentActiveSession()
      const currentMessage = currentSession?.messages.find(m => m.id === messageId)
      const messageContent = currentMessage?.content || targetMessage.content
      
      // そのメッセージより後のすべてのメッセージを削除
      const messagesToDelete = session.messages.slice(messageIndex + 1)
      for (const msg of messagesToDelete) {
        consultationStore.deleteMessage(session.id, msg.id)
      }
      
      // 注意: 対象のメッセージは削除しない（位置を保持するため）
      
      // 削除後のメッセージ履歴を取得（編集されたメッセージの直前まで）
      const updatedSession = consultationStore.getCurrentActiveSession()
      const messagesUpToTarget = updatedSession?.messages.slice(0, messageIndex) || []
      
      // リクエストの構築
      const personaSettings = options?.conferenceSettings?.enabled 
        ? undefined
        : (options?.personaSettings || consultationStore.personaSettings || undefined)
      
      // ユーザー設定をコンテキストに追加
      let userContext = ''
      if (consultationStore.userSettings?.enabled) {
        const userName = consultationStore.userSettings.name || 'ユーザー'
        userContext = `【ユーザー情報】\n名前: ${userName}\n`
        if (consultationStore.userSettings.profile) {
          userContext += `プロフィール: ${consultationStore.userSettings.profile}\n`
        }
        userContext += '\n'
      }

      // ロールプレイ設定をコンテキストに追加
      let roleplayContext = ''
      if (consultationStore.roleplaySettings?.enabled) {
        roleplayContext = `【ロールプレイモード】\n`
        roleplayContext += `このセッションはロールプレイモードです。相談ではなく、その場面を演じてください。\n`
        if (consultationStore.roleplaySettings.location) {
          roleplayContext += `場所・時間: ${consultationStore.roleplaySettings.location}\n`
        }
        if (consultationStore.roleplaySettings.situation) {
          roleplayContext += `シチュエーション: ${consultationStore.roleplaySettings.situation}\n`
        }
        roleplayContext += `キャラクターは自然にその場にいるように振る舞い、会話を進めてください。\n\n`
      }

      // 元のメッセージに添付されていた画像/音声を取得
      const messageImages = currentMessage?.images || targetMessage.images

      // デバッグ: 再送信時の画像データ確認
      console.log('[retryUserMessage] Images count:', messageImages?.length || 0)
      if (messageImages && messageImages.length > 0) {
        messageImages.forEach((img, i) => {
          console.log(`[retryUserMessage] Image ${i}: ${img.substring(0, 50)}... (length: ${img.length})`)
        })
      }

      const input: ConsultationInput = {
        message: messageContent,
        images: messageImages,  // 元メッセージの画像/音声を含める
        sessionId: session.id,
        personaSettings,
        conferenceSettings: options?.conferenceSettings,
        roleplaySettings: consultationStore.roleplaySettings || undefined,
        context: {
          ...options?.context,
          customContext: roleplayContext + userContext + (options?.context?.customContext || '')
        },
        options: {
          criticalMode: options?.criticalMode,
          useGrounding: options?.useGrounding,
          // 再生成時は微小なランダム性を追加して異なる応答を促す
          temperature: (options?.temperature ?? 0.8) + (Math.random() * 0.02 - 0.01),
          maxTokens: 60000,
          useGemini3Preview: consultationStore.settings?.useGemini3Preview ?? false,
          useGemini3FlashPreview: consultationStore.settings?.useGemini3FlashPreview ?? false,
          useGemini3FlashLitePreview: consultationStore.settings?.useGemini3FlashLitePreview ?? false,
          avoidElements: options?.avoidElements
        },
        chatHistory: messagesUpToTarget
      }

      // APIリクエスト
      const response = await apiClient.consultationChat({ ...input, modelId })

      if (response.success && response.data) {
        // アシスタントメッセージを追加
        consultationStore.addMessage(session.id, response.data.message)
        
        const result: ConsultationResult = {
          message: response.data.message,
          sessionId: response.data.sessionId,
          metadata: {
            processingTime: undefined,
            modelUsed: response.data.metadata?.modelUsed,
            tokenCount: response.data.metadata?.tokenCount
          }
        }
        
        return result
      } else {
        throw new Error('応答の生成に失敗しました')
      }
      
    } catch (err) {
      // ApiErrorの場合、次のAPIキーの存在を知らせる
      if (err instanceof ApiError && err.isRateLimit) {
        const nextApiKey = settingsStore.getNextAvailableApiKey()
        if (nextApiKey) {
          const errorMessage = `レート制限エラー: 別のAPIキー「${nextApiKey.name}」が利用可能です。設定画面で切り替えてください。`
          setError(errorMessage)
        } else {
          setError('レート制限エラー: 利用可能なAPIキーがありません。しばらくお待ちください。')
        }
      } else {
        const errorMessage = err instanceof Error ? err.message : '不明なエラーが発生しました'
        setError(errorMessage)
      }
      
      console.error('Retry message error:', err)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [consultationStore, settingsStore, modelId])
  
  const regenerateAIMessage = useCallback(async (
    messageId: string,
    options?: {
      personaSettings?: ConsultationInput['personaSettings']
      conferenceSettings?: ConsultationInput['conferenceSettings']
      context?: ConsultationInput['context']
      criticalMode?: boolean
      useGrounding?: boolean
      temperature?: number
      avoidElements?: string[]
    }
  ): Promise<ConsultationResult | null> => {
    setIsLoading(true)
    setError(null)
    
    const session = consultationStore.getCurrentActiveSession()
    if (!session || session.messages.length === 0) {
      setError('再生成するメッセージがありません')
      setIsLoading(false)
      return null
    }
    
    // 指定されたメッセージを探す
    const messageIndex = session.messages.findIndex(m => m.id === messageId)
    if (messageIndex === -1) {
      setError('メッセージが見つかりません')
      setIsLoading(false)
      return null
    }
    
    const targetMessage = session.messages[messageIndex]
    if (targetMessage.role !== 'assistant') {
      setError('AIメッセージのみ再生成できます')
      setIsLoading(false)
      return null
    }
    
    // そのメッセージより後のすべてのメッセージを削除
    const messagesToDelete = session.messages.slice(messageIndex)
    for (const msg of messagesToDelete) {
      consultationStore.deleteMessage(session.id, msg.id)
    }
    
    // 直前のユーザーメッセージを探す
    const lastUserMessage = session.messages.slice(0, messageIndex).findLast(m => m.role === 'user')
    if (!lastUserMessage) {
      setError('ユーザーメッセージが見つかりません')
      setIsLoading(false)
      return null
    }
    
    try {
      // APIキーの確認
      if (!settingsStore.isApiKeySet()) {
        throw new Error('APIキーが設定されていません')
      }
      
      // 削除後のメッセージ履歴を取得（AIメッセージ削除後）
      const updatedSession = consultationStore.getCurrentActiveSession()
      const messagesForHistory = updatedSession?.messages || []
      
      // ペルソナ設定
      const personaSettings = options?.conferenceSettings?.enabled 
        ? undefined
        : (options?.personaSettings || consultationStore.personaSettings || undefined)
      
      // ユーザー設定をコンテキストに追加
      let userContext = ''
      if (consultationStore.userSettings?.enabled) {
        const userName = consultationStore.userSettings.name || 'ユーザー'
        userContext = `【ユーザー情報】\n名前: ${userName}\n`
        if (consultationStore.userSettings.profile) {
          userContext += `プロフィール: ${consultationStore.userSettings.profile}\n`
        }
        userContext += '\n'
      }

      // ロールプレイ設定をコンテキストに追加
      let roleplayContext = ''
      if (consultationStore.roleplaySettings?.enabled) {
        roleplayContext = `【ロールプレイモード】\n`
        roleplayContext += `このセッションはロールプレイモードです。相談ではなく、その場面を演じてください。\n`
        if (consultationStore.roleplaySettings.location) {
          roleplayContext += `場所・時間: ${consultationStore.roleplaySettings.location}\n`
        }
        if (consultationStore.roleplaySettings.situation) {
          roleplayContext += `シチュエーション: ${consultationStore.roleplaySettings.situation}\n`
        }
        roleplayContext += `キャラクターは自然にその場にいるように振る舞い、会話を進めてください。\n\n`
      }

      // 温度パラメータにランダム性を追加
      const regenerateTemp = (options?.temperature ?? 0.8) + (Math.random() * 0.02 - 0.01)

      const input: ConsultationInput = {
        message: lastUserMessage.content,
        images: lastUserMessage.images,  // 直前ユーザーメッセージの画像/音声を含める
        sessionId: session.id,
        personaSettings,
        conferenceSettings: options?.conferenceSettings,
        roleplaySettings: consultationStore.roleplaySettings || undefined,
        context: {
          ...options?.context,
          customContext: roleplayContext + userContext + (options?.context?.customContext || '')
        },
        options: {
          criticalMode: options?.criticalMode,
          useGrounding: options?.useGrounding,
          temperature: regenerateTemp,
          maxTokens: 60000,
          useGemini3Preview: consultationStore.settings?.useGemini3Preview ?? false,
          useGemini3FlashPreview: consultationStore.settings?.useGemini3FlashPreview ?? false,
          useGemini3FlashLitePreview: consultationStore.settings?.useGemini3FlashLitePreview ?? false,
          avoidElements: options?.avoidElements
        },
        chatHistory: messagesForHistory
      }

      // APIリクエスト
      const response = await apiClient.consultationChat({ ...input, modelId })

      if (response.success && response.data) {
        // アシスタントメッセージを追加
        consultationStore.addMessage(session.id, response.data.message)
        
        const result: ConsultationResult = {
          message: response.data.message,
          sessionId: response.data.sessionId,
          metadata: {
            processingTime: undefined,
            modelUsed: response.data.metadata?.modelUsed,
            tokenCount: response.data.metadata?.tokenCount
          }
        }
        
        return result
      } else {
        throw new Error('応答の生成に失敗しました')
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '不明なエラーが発生しました'
      setError(errorMessage)
      console.error('Regenerate AI message error:', err)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [consultationStore, settingsStore, modelId])
  
  const clearError = useCallback(() => {
    setError(null)
  }, [])
  
  return {
    sendMessage,
    regenerateLastMessage,
    retryUserMessage,
    regenerateAIMessage,
    isLoading,
    error,
    clearError,
    activeSession: consultationStore.getCurrentActiveSession(),
    sessions: consultationStore.sessions,
    createSession: consultationStore.createSession,
    loadSession: consultationStore.loadSession,
    deleteSession: consultationStore.deleteSession,
    updateSessionTitle: consultationStore.updateSessionTitle,
    clearAllSessions: consultationStore.clearAllSessions,
    setPersona: consultationStore.setPersona,
    clearPersona: consultationStore.clearPersona,
    personaSettings: consultationStore.personaSettings
  }
}
